import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Clock3, Plus, Trash2, Moon, Sun } from 'lucide-react';
import CuadranteCard from './CuadranteCard';

const TurnosTab = () => {
  const [turnos, setTurnos] = useState([]);
  const [nuevo, setNuevo] = useState({ nombre: '', hora_inicio: '09:00', hora_fin: '18:00' });

  const cargar = () => api.get('/admin/turnos').then((r) => setTurnos(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const crear = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/turnos', nuevo);
      toast.success('Turno creado');
      setNuevo({ nombre: '', hora_inicio: '09:00', hora_fin: '18:00' });
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear turno');
    }
  };

  const eliminar = async (t) => {
    try {
      await api.delete(`/admin/turnos/${t.id}`);
      toast.success('Turno eliminado');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6" data-testid="turnos-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="border-zinc-200 lg:col-span-1 h-fit">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Plus className="h-5 w-5" /> Nuevo turno
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={crear} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre del turno</Label>
              <Input data-testid="new-turno-nombre" value={nuevo.nombre} placeholder="Ej: Mañana, Noche, Partida"
                onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Hora inicio</Label>
                <Input type="time" data-testid="new-turno-inicio" value={nuevo.hora_inicio}
                  onChange={(e) => setNuevo({ ...nuevo, hora_inicio: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Hora fin</Label>
                <Input type="time" data-testid="new-turno-fin" value={nuevo.hora_fin}
                  onChange={(e) => setNuevo({ ...nuevo, hora_fin: e.target.value })} required />
              </div>
            </div>
            <p className="text-xs text-zinc-400">Si la hora fin es menor que la de inicio, el turno cruza la medianoche (nocturno).</p>
            <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="create-turno-btn">
              Crear turno
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 lg:col-span-2">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Clock3 className="h-5 w-5" /> Turnos de la empresa ({turnos.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  <th className="py-3 px-5">Turno</th>
                  <th className="py-3 px-5">Horario</th>
                  <th className="py-3 px-5">Tipo</th>
                  <th className="py-3 px-5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {turnos.map((t) => (
                  <tr key={t.id} className="border-t border-zinc-100" data-testid={`turno-row-${t.id}`}>
                    <td className="py-3 px-5 font-medium text-zinc-900">{t.nombre}</td>
                    <td className="py-3 px-5 tabular-nums">{t.hora_inicio} – {t.hora_fin}</td>
                    <td className="py-3 px-5">
                      {t.cruza_medianoche
                        ? <Badge className="bg-indigo-100 text-indigo-800 gap-1"><Moon className="h-3.5 w-3.5" /> Nocturno</Badge>
                        : <Badge variant="outline" className="gap-1"><Sun className="h-3.5 w-3.5" /> Diurno</Badge>}
                    </td>
                    <td className="py-3 px-5">
                      <div className="flex justify-end">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="outline" className="text-red-600" data-testid={`delete-turno-${t.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar el turno &quot;{t.nombre}&quot;?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Los fichajes ya registrados conservan su clasificación. Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(t)}
                                data-testid={`confirm-delete-turno-${t.id}`}>Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
                {turnos.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-zinc-400">
                    Aún no has definido turnos. Sin turnos, se usa la clasificación por defecto (mañana/tarde/día).
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      </div>
      <CuadranteCard />
    </div>
  );
};

export default TurnosTab;
